import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { IconPickerComponent } from '@parashift/shared/components';
import { DashboardWidget } from '@parashift/shared/models';
import { WidgetHostComponent } from '../../landing-page/widget-host/widget-host.component';
import { WIDGET_REGISTRY } from '../../landing-page/widget-host/widget-registry';
import { WidgetConfig } from '../../landing-page/models/widget-config.model';
import { WidgetType } from '../../landing-page/models/widget-type';
import { WIDGET_SETTINGS_DOCS } from '../widget-settings-docs';

/** The collected form values, handed back to `DashboardAdminComponent` to persist. */
export interface WidgetEditorResult {
  id: string | null; // null = new
  title: string;
  widgetType: string;
  icon: string;
  cols: number;
  position: number;
  requiredRoles: string[]; // Permission identifiers; [] = everyone
  settings: Record<string, unknown>;
}

/** What `DashboardAdminComponent` hands in via `initialState.model` when opening the modal. */
export interface WidgetEditorInput {
  editingWidget: DashboardWidget | null; // null = adding a new widget
  nextPosition: number; // default `position` for a new widget
}

// Only gathers and validates input — DashboardAdminComponent performs the actual save,
// since only it knows the parent template's id and how to report banners/reload.
@Component({
  selector: 'widget-editor-modal',
  templateUrl: './widget-editor-modal.component.html',
  styleUrls: ['./widget-editor-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, IconPickerComponent, WidgetHostComponent]
})
export class WidgetEditorModalComponent implements OnInit {
  bsModalRef = inject(BsModalRef);

  // Holds WidgetEditorInput on open, then WidgetEditorResult once save() overwrites it.
  model: unknown = null;
  /** Read by `ModalService` on close — true only when Save was clicked. */
  state = false;

  private editingWidget: DashboardWidget | null = null;

  // Derived from WIDGET_REGISTRY (the single place mapping a type to its component) instead
  // of a second, hand-maintained list that could drift out of sync with it.
  readonly widgetTypes = Object.keys(WIDGET_REGISTRY) as WidgetType[];

  readonly isEdit = computed(() => !!this.editingWidget);

  readonly title = signal('');
  readonly widgetType = signal<WidgetType>(WidgetType.KeyValue);
  readonly icon = signal('');
  readonly cols = signal(6);
  readonly position = signal(0);
  readonly requiredRoles = signal<string[]>([]);
  readonly settingsJson = signal('{}');
  readonly error = signal<string | null>(null);
  readonly preview = signal<WidgetConfig | null>(null);

  ngOnInit(): void {
    const input = this.model as WidgetEditorInput | null;
    this.editingWidget = input?.editingWidget ?? null;

    const widget = this.editingWidget;
    if (!widget) {
      this.position.set(input?.nextPosition ?? 0);
      return;
    }
    this.title.set(widget.title);
    // widget_type is a plain string at the API-model boundary (unconstrained in the DB);
    // narrowed here to WidgetType.
    this.widgetType.set(widget.widget_type as WidgetType);
    this.icon.set(widget.icon ?? '');
    this.setCols(Number(widget.grid?.['cols']));
    this.position.set(widget.position ?? 0);
    this.requiredRoles.set([...(widget.required_roles ?? [])]);
    this.settingsJson.set(JSON.stringify(widget.settings ?? {}, null, 2));
  }

  // The number input's min/max attributes don't block a typed-in out-of-range value.
  setCols(value: number): void {
    this.cols.set(Math.min(Math.max(Number(value) || 6, 1), 12));
  }

  hasRole(role: string): boolean {
    return this.requiredRoles().includes(role);
  }

  toggleRole(role: string): void {
    this.requiredRoles.set(
      this.hasRole(role) ? this.requiredRoles().filter(existing => existing !== role) : [...this.requiredRoles(), role]
    );
  }

  setEveryone(): void {
    this.requiredRoles.set([]);
  }

  settingsDoc(): { description: string; examples: { label: string; json: string }[] } | null {
    const doc = WIDGET_SETTINGS_DOCS[this.widgetType()];
    if (!doc) {
      return null;
    }
    return {
      description: doc.description,
      examples: doc.examples.map(example => ({ label: example.label, json: JSON.stringify(example.value, null, 2) }))
    };
  }

  insertExample(json: string): void {
    this.settingsJson.set(json);
  }

  previewWidget(): void {
    let settings: Record<string, unknown>;
    try {
      settings = JSON.parse(this.settingsJson() || '{}');
    } catch {
      this.error.set($localize`:@@pdc.dashboard_admin.msg.invalid_json_preview:Settings is not valid JSON — fix it to preview.`);
      this.preview.set(null);
      return;
    }
    this.error.set(null);
    this.preview.set({
      id: 'preview',
      type: this.widgetType(),
      title: this.title(),
      grid: { cols: Number(this.cols()) || 6 },
      settings
    } as unknown as WidgetConfig);
  }

  save(): void {
    let settings: Record<string, unknown>;
    try {
      settings = JSON.parse(this.settingsJson() || '{}');
    } catch {
      this.error.set($localize`:@@pdc.dashboard_admin.msg.invalid_json:Settings is not valid JSON.`);
      return;
    }

    this.model = {
      id: this.editingWidget?.id ?? null,
      title: this.title(),
      widgetType: this.widgetType(),
      icon: this.icon(),
      cols: Number(this.cols()) || 6,
      position: Number(this.position()) || 0,
      requiredRoles: this.requiredRoles(),
      settings
    };
    this.state = true;
    this.bsModalRef.hide();
  }

  close(): void {
    this.bsModalRef.hide();
  }
}
